import React, { useState, useEffect } from 'react';
import { Modal, Upload, Button, Form, Input, InputNumber, Typography, Space, message, Divider, List, Spin, Alert, Switch } from 'antd';
import { UploadOutlined, FileExcelOutlined, SettingOutlined, LoadingOutlined, BugOutlined, PlayCircleOutlined, FormOutlined } from '@ant-design/icons';
import api from '../../../../services/api';

const { Text, Title } = Typography;
const { TextArea } = Input;

const ExecutionSetupModal = ({ open, onCancel, onExecute, nodes, edges = [], isDebugMode = false, debugNodeName = null }) => {
    const [form] = Form.useForm();
    const [excelNodes, setExcelNodes] = useState([]);
    const [inputNodes, setInputNodes] = useState([]); // Input nodes that need user input
    const [fileList, setFileList] = useState({}); // Map nodeId -> file object
    const [uploadedFiles, setUploadedFiles] = useState({}); // Map nodeId -> { fileId, path }
    const [uploading, setUploading] = useState({}); // Map nodeId -> boolean
    const [processing, setProcessing] = useState(false);
    const [inputValues, setInputValues] = useState({}); // Map nodeId -> { fieldId -> value }
    const [inputFileList, setInputFileList] = useState({}); // Map nodeId -> { fieldId -> file }

    useEffect(() => {
        if (open && nodes) {
            console.log('ExecutionSetupModal: Processing nodes', nodes.length);
            
            // Find all Excel Input nodes
            // Priority: data.nodeType > data.type > type (for React Flow compatibility)
            const excelInputs = nodes.filter(n => {
                const nodeType = n.data?.nodeType || n.data?.type || n.type;
                return nodeType === 'excel_input';
            });
            setExcelNodes(excelInputs);
            console.log('ExecutionSetupModal: Found excel nodes', excelInputs.length);

            // Find Input nodes that have inputFields configured AND are isolated (no edges in or out)
            // These are standalone input nodes that need user input at execution time
            const inputNodesWithFields = nodes.filter(n => {
                const nodeType = n.data?.nodeType || n.data?.type || n.type;
                if (nodeType !== 'input') return false;
                
                // Check if node has inputFields configured
                const inputFields = n.data?.config?.inputFields || [];
                if (inputFields.length === 0) return false;

                // Check if this node has no incoming edges AND no outgoing edges (is isolated)
                const hasIncomingEdge = edges.some(e => e.target === n.id);
                const hasOutgoingEdge = edges.some(e => e.source === n.id);
                
                console.log('ExecutionSetupModal: Node', n.id, 'nodeType:', nodeType, 
                    'hasIncoming:', hasIncomingEdge, 'hasOutgoing:', hasOutgoingEdge,
                    'inputFields:', inputFields.length);
                
                // Only include nodes that have no edges at all (isolated nodes)
                return !hasIncomingEdge && !hasOutgoingEdge;
            });
            setInputNodes(inputNodesWithFields);
            console.log('ExecutionSetupModal: Found isolated input nodes with fields', inputNodesWithFields.length);
            
            // Reset state
            setFileList({});
            setUploadedFiles({});
            setUploading({});
            setInputValues({});
            setInputFileList({});
            form.resetFields();

            // Initialize input values with default values
            const initialValues = {};
            inputNodesWithFields.forEach(node => {
                const fields = node.data?.config?.inputFields || [];
                initialValues[node.id] = {};
                fields.forEach(field => {
                    if (field.defaultValue) {
                        initialValues[node.id][field.id] = field.defaultValue;
                        initialValues[node.id][field.variableName] = field.defaultValue;
                    }
                });
            });
            if (Object.keys(initialValues).length > 0) {
                setInputValues(initialValues);
            }
        }
    }, [open, nodes, edges, form]);

    const handleFileSelect = (nodeId, file) => {
        // Update file list for UI
        setFileList(prev => ({
            ...prev,
            [nodeId]: file
        }));
        
        // Clear uploaded status if file changes
        setUploadedFiles(prev => {
            const newState = { ...prev };
            delete newState[nodeId];
            return newState;
        });
        
        return false; // Prevent auto upload
    };

    const handleExecute = async () => {
        try {
            const values = await form.validateFields();
            
            // Check if all required files are selected
            const missingFiles = excelNodes.filter(node => !fileList[node.id]);
            if (missingFiles.length > 0) {
                message.error(`Please select files for: ${missingFiles.map(n => n.data?.label || n.id).join(', ')}`);
                return;
            }

            setProcessing(true);

            const currentUploadedFiles = { ...uploadedFiles };

            // Upload files that haven't been uploaded yet
            for (const node of excelNodes) {
                const file = fileList[node.id];
                if (!file) continue;
                
                // Skip if already uploaded and file hasn't changed
                if (currentUploadedFiles[node.id]) continue;

                setUploading(prev => ({ ...prev, [node.id]: true }));
                
                const formData = new FormData();
                formData.append('file', file);
                
                try {
                    const response = await api.post('/workflow/upload-temp', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    });
                    
                    if (response.data && response.data.fileId) {
                        const fileInfo = {
                            fileId: response.data.fileId,
                            path: response.data.path,
                            originalName: file.name
                        };
                        currentUploadedFiles[node.id] = fileInfo;
                        setUploadedFiles(prev => ({
                            ...prev,
                            [node.id]: fileInfo
                        }));
                    } else {
                        throw new Error('Invalid response from server');
                    }
                } catch (err) {
                    console.error('Error uploading file:', err);
                    message.error(`Failed to upload ${file.name}`);
                    setProcessing(false);
                    setUploading(prev => ({ ...prev, [node.id]: false }));
                    return;
                } finally {
                    setUploading(prev => ({ ...prev, [node.id]: false }));
                }
            }

            // Prepare execution context
            const executionContext = {
                variables: values.variables || {},
                nodeOverrides: {},
                inputNodeValues: {} // Values from input nodes
            };

            // Inject uploaded file info into node overrides
            Object.entries(currentUploadedFiles).forEach(([nodeId, fileInfo]) => {
                executionContext.nodeOverrides[nodeId] = {
                    fileId: fileInfo.fileId,
                    filePath: fileInfo.path,
                    originalName: fileInfo.originalName
                };
            });

            // Inject input node values
            Object.entries(inputValues).forEach(([nodeId, fieldValues]) => {
                executionContext.inputNodeValues[nodeId] = fieldValues;
            });

            // TODO: Handle file uploads from input nodes if needed
            // For now, just pass the file objects (they would need to be uploaded similarly to excel files)

            onExecute(executionContext);
            setProcessing(false);
        } catch (error) {
            console.error('Setup error:', error);
            setProcessing(false);
        }
    };

    return (
        <Modal
            title={
                <Space>
                    {isDebugMode ? <BugOutlined style={{ color: '#fa8c16' }} /> : <PlayCircleOutlined />}
                    <span>{isDebugMode ? 'Debug Setup' : 'Execution Setup'}</span>
                </Space>
            }
            open={open}
            onCancel={onCancel}
            onOk={handleExecute}
            okText={isDebugMode ? 'Start Debug' : 'Run Workflow'}
            okButtonProps={{
                style: isDebugMode ? { backgroundColor: '#fa8c16', borderColor: '#fa8c16' } : {}
            }}
            confirmLoading={processing}
            width={600}
        >
            {isDebugMode && debugNodeName && (
                <Alert
                    type="warning"
                    icon={<BugOutlined />}
                    message={`Debug Mode: Execute to "${debugNodeName}"`}
                    description="Workflow will execute up to and pause at the selected node. You can step through remaining nodes after."
                    style={{ marginBottom: 16 }}
                    showIcon
                />
            )}
            
            <div style={{ marginBottom: 16 }}>
                <Text type="secondary">
                    Configure inputs and variables before {isDebugMode ? 'debugging' : 'executing'} the workflow.
                </Text>
            </div>

            {excelNodes.length > 0 && (
                <>
                    <Divider orientation="left" style={{ fontSize: '14px' }}>
                        <Space><FileExcelOutlined /> Excel Inputs</Space>
                    </Divider>
                    
                    <List
                        dataSource={excelNodes}
                        renderItem={node => (
                            <List.Item>
                                <div style={{ width: '100%' }}>
                                    <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                                        <Text strong>{node.data?.label || `Excel Input (${node.id})`}</Text>
                                        {uploading[node.id] ? (
                                            <Text type="warning" style={{ fontSize: '12px' }}>
                                                <Spin indicator={<LoadingOutlined style={{ fontSize: 12 }} spin />} /> Uploading...
                                            </Text>
                                        ) : uploadedFiles[node.id] ? (
                                            <Text type="success" style={{ fontSize: '12px' }}>
                                                Ready (Uploaded)
                                            </Text>
                                        ) : fileList[node.id] ? (
                                            <Text type="info" style={{ fontSize: '12px' }}>
                                                Ready to upload
                                            </Text>
                                        ) : null}
                                    </div>
                                    <Upload
                                        accept=".xlsx, .xls, .csv"
                                        showUploadList={false}
                                        beforeUpload={(file) => handleFileSelect(node.id, file)}
                                    >
                                        <Button icon={<UploadOutlined />} block loading={uploading[node.id]}>
                                            {fileList[node.id] ? fileList[node.id].name : 'Select File'}
                                        </Button>
                                    </Upload>
                                </div>
                            </List.Item>
                        )}
                    />
                </>
            )}

            {inputNodes.length > 0 && (
                <>
                    <Divider orientation="left" style={{ fontSize: '14px' }}>
                        <Space><FormOutlined /> Input Fields</Space>
                    </Divider>
                    
                    {inputNodes.map(node => {
                        const inputFields = node.data?.config?.inputFields || [];
                        return (
                            <div key={node.id} style={{ marginBottom: 16 }}>
                                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                    {node.data?.label || `Input (${node.id})`}
                                </Text>
                                {inputFields.map(field => (
                                    <div key={field.id} style={{ marginBottom: 12 }}>
                                        <Text style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: 4 }}>
                                            {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
                                        </Text>
                                        {field.inputType === 'text' && (
                                            <Input
                                                placeholder={field.placeholder}
                                                defaultValue={field.defaultValue}
                                                value={inputValues[node.id]?.[field.id]}
                                                onChange={(e) => setInputValues(prev => ({
                                                    ...prev,
                                                    [node.id]: {
                                                        ...prev[node.id],
                                                        [field.id]: e.target.value,
                                                        [`${field.variableName}`]: e.target.value
                                                    }
                                                }))}
                                            />
                                        )}
                                        {field.inputType === 'textarea' && (
                                            <TextArea
                                                rows={3}
                                                placeholder={field.placeholder}
                                                defaultValue={field.defaultValue}
                                                value={inputValues[node.id]?.[field.id]}
                                                onChange={(e) => setInputValues(prev => ({
                                                    ...prev,
                                                    [node.id]: {
                                                        ...prev[node.id],
                                                        [field.id]: e.target.value,
                                                        [`${field.variableName}`]: e.target.value
                                                    }
                                                }))}
                                            />
                                        )}
                                        {field.inputType === 'number' && (
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                placeholder={field.placeholder}
                                                defaultValue={field.defaultValue ? Number(field.defaultValue) : undefined}
                                                min={field.validation?.min}
                                                max={field.validation?.max}
                                                value={inputValues[node.id]?.[field.id]}
                                                onChange={(value) => setInputValues(prev => ({
                                                    ...prev,
                                                    [node.id]: {
                                                        ...prev[node.id],
                                                        [field.id]: value,
                                                        [`${field.variableName}`]: value
                                                    }
                                                }))}
                                            />
                                        )}
                                        {(field.inputType === 'csv_upload' || field.inputType === 'file_upload') && (
                                            <Upload
                                                accept={field.fileConfig?.acceptedTypes || '.csv,.xlsx,.json'}
                                                showUploadList={false}
                                                beforeUpload={(file) => {
                                                    setInputFileList(prev => ({
                                                        ...prev,
                                                        [node.id]: {
                                                            ...prev[node.id],
                                                            [field.id]: file
                                                        }
                                                    }));
                                                    return false;
                                                }}
                                            >
                                                <Button icon={<UploadOutlined />} block>
                                                    {inputFileList[node.id]?.[field.id]?.name || 'Select File'}
                                                </Button>
                                            </Upload>
                                        )}
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </>
            )}

            <Divider orientation="left" style={{ fontSize: '14px' }}>
                <Space><SettingOutlined /> Environment Variables</Space>
            </Divider>

            <Form form={form} layout="vertical">
                <Form.List name="variables">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'key']}
                                        rules={[{ required: true, message: 'Missing key' }]}
                                    >
                                        <Input placeholder="Key (e.g. API_KEY)" />
                                    </Form.Item>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'value']}
                                        rules={[{ required: true, message: 'Missing value' }]}
                                    >
                                        <Input placeholder="Value" />
                                    </Form.Item>
                                    <Button onClick={() => remove(name)} type="text" danger>
                                        Remove
                                    </Button>
                                </Space>
                            ))}
                            <Form.Item>
                                <Button type="dashed" onClick={() => add()} block icon={<SettingOutlined />}>
                                    Add Variable
                                </Button>
                            </Form.Item>
                        </>
                    )}
                </Form.List>
            </Form>
        </Modal>
    );
};

export default ExecutionSetupModal;
